import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

const searchPluginKey = new PluginKey("searchHighlight")

const MAX_MATCHES = 2000

function findMatches(doc, query, caseSensitive) {
  if (!query || query.length === 0) return { matches: [], truncated: false }

  const matches = []
  const flags = caseSensitive ? "g" : "gi"
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const regex = new RegExp(escaped, flags)

  doc.descendants((node, pos) => {
    if (!node.isTextblock || matches.length > MAX_MATCHES) return
    // Join text within a paragraph so inline marks do not interrupt a phrase.
    // Inline atoms remain boundaries; a match must never replace through one.
    let text = ""
    const positions = []
    node.descendants((child, offset) => {
      if (child.isText) {
        for (let i = 0; i < child.text.length; i++) positions.push(pos + 1 + offset + i)
        text += child.text
      } else if (child.isLeaf) {
        text += "\ufffc"
        positions.push(pos + 1 + offset)
      }
    })
    regex.lastIndex = 0
    let match
    while ((match = regex.exec(text)) !== null) {
      if (match[0].includes("\ufffc")) continue
      matches.push({
        from: positions[match.index],
        to: positions[match.index + match[0].length - 1] + 1,
      })
      if (matches.length > MAX_MATCHES) break
      if (match[0].length === 0) { regex.lastIndex++; break }
    }
    return false
  })

  return { matches: matches.slice(0, MAX_MATCHES), truncated: matches.length > MAX_MATCHES }
}

function buildDecorations(doc, matches, currentIndex) {
  if (matches.length === 0) return DecorationSet.empty

  const decorations = matches.map((m, i) => {
    const className = i === currentIndex ? "search-match search-match-current" : "search-match"
    return Decoration.inline(m.from, m.to, { class: className })
  })

  return DecorationSet.create(doc, decorations)
}

const SearchHighlight = Extension.create({
  name: "searchHighlight",

  addStorage() {
    return {
      query: "",
      caseSensitive: false,
      matches: [],
      currentIndex: -1,
      truncated: false,
    }
  },

  addCommands() {
    return {
      setSearchQuery: ({ query, caseSensitive }) => ({ state, dispatch, tr }) => {
        if (dispatch) {
          tr.setMeta(searchPluginKey, { type: "search", query, caseSensitive })
          dispatch(tr)
        }
        return true
      },

      nextMatch: () => ({ state, dispatch, tr }) => {
        const storage = this.storage
        if (storage.matches.length === 0) return false
        const next = (storage.currentIndex + 1) % storage.matches.length
        if (dispatch) {
          tr.setMeta(searchPluginKey, { type: "navigate", index: next })
          dispatch(tr)
        }
        return true
      },

      prevMatch: () => ({ state, dispatch, tr }) => {
        const storage = this.storage
        if (storage.matches.length === 0) return false
        const prev = (storage.currentIndex - 1 + storage.matches.length) % storage.matches.length
        if (dispatch) {
          tr.setMeta(searchPluginKey, { type: "navigate", index: prev })
          dispatch(tr)
        }
        return true
      },

      clearSearch: () => ({ state, dispatch, tr }) => {
        if (dispatch) {
          tr.setMeta(searchPluginKey, { type: "clear" })
          dispatch(tr)
        }
        return true
      },

      replaceCurrentMatch: (replacement) => ({ state, dispatch, tr, editor }) => {
        const storage = this.storage
        if (storage.matches.length === 0 || storage.currentIndex < 0) return false
        const match = storage.matches[storage.currentIndex]
        if (!match) return false

        if (dispatch) {
          tr.insertText(replacement, match.from, match.to)
          dispatch(tr)
        }
        // The plugin refreshes matches synchronously on the document transaction.
        return true
      },

      replaceAllMatches: (replacement) => ({ state, dispatch, tr, editor }) => {
        const storage = this.storage
        if (storage.matches.length === 0 || storage.truncated) return false

        if (dispatch) {
          // Replace from end to start to preserve positions
          const sorted = [...storage.matches].sort((a, b) => b.from - a.from)
          sorted.forEach(match => {
            tr.insertText(replacement, match.from, match.to)
          })
          dispatch(tr)
        }
        return true
      },
    }
  },

  addProseMirrorPlugins() {
    const extensionStorage = this.storage

    return [
      new Plugin({
        key: searchPluginKey,

        state: {
          init() { return DecorationSet.empty },
          apply(tr, oldSet, oldState, newState) {
            const meta = tr.getMeta(searchPluginKey)

            if (meta) {
              if (meta.type === "clear") {
                extensionStorage.query = ""
                extensionStorage.matches = []
                extensionStorage.currentIndex = -1
                extensionStorage.truncated = false
                return DecorationSet.empty
              }

              if (meta.type === "search") {
                extensionStorage.query = meta.query
                extensionStorage.caseSensitive = meta.caseSensitive
                Object.assign(extensionStorage, findMatches(newState.doc, meta.query, meta.caseSensitive))
                extensionStorage.currentIndex = extensionStorage.matches.length > 0 ? 0 : -1
                return buildDecorations(newState.doc, extensionStorage.matches, extensionStorage.currentIndex)
              }

              if (meta.type === "navigate") {
                extensionStorage.currentIndex = meta.index
                return buildDecorations(newState.doc, extensionStorage.matches, extensionStorage.currentIndex)
              }
            }

            // Rebuild on doc changes if there's an active search
            if (tr.docChanged && extensionStorage.query) {
              Object.assign(extensionStorage, findMatches(newState.doc, extensionStorage.query, extensionStorage.caseSensitive))
              // Clamp currentIndex
              if (extensionStorage.currentIndex >= extensionStorage.matches.length) {
                extensionStorage.currentIndex = extensionStorage.matches.length > 0 ? 0 : -1
              }
              return buildDecorations(newState.doc, extensionStorage.matches, extensionStorage.currentIndex)
            }

            return oldSet.map(tr.mapping, tr.doc)
          },
        },

        props: {
          decorations(state) {
            return this.getState(state)
          },
        },
      }),
    ]
  },
})

export { SearchHighlight, searchPluginKey }
